import React, { useState, useMemo } from 'react';

import {
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  LinkOutlined,
  CheckOutlined,
  RollbackOutlined,
  UndoOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import VideoPreviewRaw from '@shared/VideoPreview';
import { Can } from '@shared/permissions';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';

// VideoPreview is .jsx; TS can't infer destructured prop types under strict mode.
// Cast to any — same workaround used by shared/VideoSection.tsx and pages/operator/ReturnReceipts/Show.tsx.
const VideoPreview = VideoPreviewRaw as any;
import { Card, Descriptions, Button, Empty, Tabs, Tag, Table, Modal, message, Tooltip, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import { formatDateTime, formatDate } from '../../../utils/dateTime';

const { Text } = Typography;

interface ReturnOrderShowViewProps {
  returnOrder: any; // BE payload type — keep `any` until the .js service is migrated. Don't try to type it fully now.
  returnOrderService?: any;
  returnReceiptService?: any;
  onReopenSuccess?: () => void;
  role?: 'admin' | 'operator';
}

/**
 * ReturnOrderShowView - 通用退貨單詳情組件
 *
 * @param {Object} props
 * @param {Object} props.returnOrder - 退貨單資料 (loaded by route loader)
 * @param {string} props.role - 角色 (admin 或 operator)
 */
export default function ReturnOrderShowView({
  returnOrder,
  returnOrderService,
  returnReceiptService,
  onReopenSuccess,
  role = 'admin',
}: ReturnOrderShowViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reopenLoadingId, setReopenLoadingId] = useState<string | null>(null);
  const [cancelLoadingId, setCancelLoadingId] = useState<string | null>(null);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [unfinalizeLoading, setUnfinalizeLoading] = useState(false);
  const attributeDefinitions = useInventoryAttributeDefinitions(role, returnOrder?.merchant_id);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map((d) => [d.name, d.label ?? d.name])),
    [attributeDefinitions]
  );

  const refetch = () => {
    if (onReopenSuccess) onReopenSuccess();
  };

  const handleEdit = () => {
    navigate(`edit`);
  };

  const handleReopen = (receiptId: string) => {
    if (!returnReceiptService || !receiptId) return;
    Modal.confirm({
      title: t('shared.return_orders.detail.continue_verify_confirm_title', { defaultValue: '繼續驗收？' }),
      content: t('shared.return_orders.detail.continue_verify_confirm_content', {
        defaultValue: '繼續驗收後本驗收單將回到「待驗收」狀態，原驗收人與驗收時間會被清除。此動作僅限尚未轉入庫時可用。',
      }),
      okText: t('shared.return_orders.detail.continue_verify_confirm_ok', { defaultValue: '確定繼續' }),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setReopenLoadingId(receiptId);
        try {
          const res = await returnReceiptService.reopen(receiptId);
          if (res.success) {
            message.success(t('shared.return_orders.detail.continue_verify_success', { defaultValue: '已回到待驗收狀態' }));
            refetch();
          } else {
            message.error(
              t(`errors.${res.message}`, {
                ...(res.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              }) as string
            );
          }
        } catch (err) {
          console.error('Failed to reopen return receipt:', err);
          message.error(t('common.error.operation_failed'));
        } finally {
          setReopenLoadingId(null);
        }
      },
    });
  };

  const handleCancel = (receiptId: string) => {
    if (!returnReceiptService || !receiptId) return;
    Modal.confirm({
      title: t('shared.return_orders.detail.void_receipt_confirm_title', {
        defaultValue: '作廢這張驗收單？',
      }),
      content: t('shared.return_orders.detail.void_receipt_confirm_content', {
        defaultValue:
          '作廢後將無法復原。若這是此退貨單最後一張未作廢的驗收單，整張退貨單將被退回「待驗收」狀態。',
      }),
      okText: t('shared.return_orders.detail.void_receipt_confirm_ok', { defaultValue: '確認作廢' }),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setCancelLoadingId(receiptId);
        try {
          const res = await returnReceiptService.cancel(receiptId);
          if (res.success) {
            message.success(
              t('shared.return_orders.detail.void_receipt_success', { defaultValue: '已作廢驗收單' }),
            );
            refetch();
          } else {
            message.error(
              t(`errors.${res.message}`, {
                ...(res.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              }) as string,
            );
          }
        } catch (err) {
          console.error('Failed to void return receipt:', err);
          message.error(t('common.error.operation_failed'));
        } finally {
          setCancelLoadingId(null);
        }
      },
    });
  };

  const hasShortReceived = (returnOrder?.received_summary || []).some((r: any) => (r.diff ?? 0) < 0);

  const handleFinalize = () => {
    if (!returnOrderService || !returnOrder?.id) return;
    Modal.confirm({
      title: t('shared.return_orders.detail.finalize_confirm_title', { defaultValue: '確認結案驗收' }),
      content: hasShortReceived
        ? t('shared.return_orders.detail.finalize_confirm_short_warning', {
            defaultValue: '部分商品實收 < 預期，確認不再有後續到貨？結案後此單將不可再新增驗收單。',
          })
        : t('shared.return_orders.detail.finalize_confirm_body', {
            defaultValue: '結案後此單將不可再新增驗收單。',
          }),
      okText: t('shared.return_orders.detail.finalize', { defaultValue: '結案驗收' }),
      cancelText: t('common.button.cancel'),
      okButtonProps: { type: 'primary', loading: finalizeLoading },
      onOk: async () => {
        setFinalizeLoading(true);
        try {
          const res = await returnOrderService.finalizeReceive(returnOrder.id);
          if (res?.success) {
            message.success(t('shared.return_orders.detail.finalize_success', { defaultValue: '已結案' }));
            refetch();
          } else {
            message.error(
              t(`errors.${res?.message}`, {
                ...(res?.interpolations || {}),
                defaultValue: t('shared.return_orders.detail.finalize_failed', { defaultValue: '結案失敗' }),
              }) as string
            );
          }
        } catch (err) {
          console.error('Failed to finalize return order:', err);
          message.error(t('shared.return_orders.detail.finalize_failed', { defaultValue: '結案失敗' }));
        } finally {
          setFinalizeLoading(false);
        }
      },
    });
  };

  const handleUnfinalize = () => {
    if (!returnOrderService || !returnOrder?.id) return;
    Modal.confirm({
      title: t('shared.return_orders.detail.unfinalize_confirm_title', { defaultValue: '撤回結案？' }),
      content: t('shared.return_orders.detail.unfinalize_confirm_body', {
        defaultValue: '撤回後此單將回到「部分驗收」狀態，可再新增驗收單。',
      }),
      okText: t('shared.return_orders.detail.unfinalize', { defaultValue: '撤回結案' }),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true, loading: unfinalizeLoading },
      onOk: async () => {
        setUnfinalizeLoading(true);
        try {
          const res = await returnOrderService.unfinalizeReceive(returnOrder.id);
          if (res?.success) {
            message.success(t('shared.return_orders.detail.unfinalize_success', { defaultValue: '已撤回結案' }));
            refetch();
          } else {
            message.error(
              t(`errors.${res?.message}`, {
                ...(res?.interpolations || {}),
                defaultValue: t('common.error.operation_failed'),
              }) as string
            );
          }
        } catch (err) {
          console.error('Failed to unfinalize return order:', err);
          message.error(t('common.error.operation_failed'));
        } finally {
          setUnfinalizeLoading(false);
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'default', icon: <SyncOutlined /> },
      partial_received: { color: 'processing', icon: <SyncOutlined /> },
      received: { color: 'processing', icon: <CheckCircleOutlined /> },
      verified: { color: 'processing', icon: <CheckCircleOutlined /> },
      done: { color: 'success', icon: <CheckCircleOutlined /> },
      canceled: { color: 'error', icon: <CloseCircleOutlined /> },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {t(`enums.return_order.status.${status}`, { defaultValue: status })}
      </Tag>
    );
  };

  if (!returnOrder) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  const itemColumns = [
    {
      title: t('models.return_order_item.product'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.product_id ? (
              <Link to={`/products/${record.product_id}`}>{record.product_name}</Link>
            ) : record.product_name}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.product_sku}</div>
        </div>
      ),
    },
    {
      title: t('models.return_order_item.inventory_type'),
      dataIndex: 'inventory_type_name',
      key: 'inventory_type_name',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.return_order_item.manufacturing_date', { defaultValue: '製造日期' }),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => v ? formatDate(v) : '-',
    },
    {
      title: t('models.return_order_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => v ? formatDate(v) : '-',
    },
    {
      title: t('models.return_order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (v: Record<string, unknown> | null) => (
        <CustomAttributesTags attributes={v} attributeLabelByName={attributeLabelByName} />
      ),
    },
    {
      title: t('models.return_order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
      render: (v: number | null) => v ?? 0,
    },
    {
      title: t('models.receipt_item.received_quantity', { defaultValue: '實收' }),
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      align: 'right' as const,
      render: (qty: number | null, record: any) => {
        const verified = qty ?? 0;
        const total = record.quantity ?? 0;
        const hasDiscrepancy = verified !== total;
        const color = !hasDiscrepancy ? '#000' : (verified > total ? '#d48806' : '#cf1322');
        return (
          <div style={{ textAlign: 'right' }}>
            <div>
              <span style={{ color, fontWeight: hasDiscrepancy ? 500 : 400 }}>{verified}</span>
              <span style={{ color: '#999', margin: '0 4px' }}>/</span>
              <span style={{ color: '#666' }}>{total}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: t('models.return_order_item.note'),
      dataIndex: 'note',
      key: 'note',
      render: (v: string | null) => v || '-',
    },
  ];

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? [
        { label: t('operator.menu.return_orders.title') },
        { label: t('operator.menu.return_orders.list'), path: '/return_orders' },
        { label: returnOrder.name },
      ]
    : ['returns', 'returns.list', { label: returnOrder.name }];

  const finalizeBlocked = role === 'operator' && returnOrder.status === 'partial_received' && !returnOrder.finalizable;

  const serialNumberColumns = useMemo(() => [
    {
      nameKey: 'models.serial_number.number',
      persist: true,
      width: '150px',
      selector: ({ number }: any) => number,
    },
    {
      nameKey: 'models.serial_number.product_sku',
      width: '150px',
      selector: ({ product_sku }: any) => product_sku || '-',
    },
    {
      nameKey: 'models.serial_number.product_name',
      width: '200px',
      wrap: true,
      selector: ({ product_name }: any) => product_name || '-',
    },
    {
      nameKey: 'common.created_at',
      width: '180px',
      selector: ({ created_at }: any) => created_at,
    },
  ], [t]);

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={breadcrumbItems}
        actions={
          <>
            {role === 'operator' && returnOrder.status === 'partial_received' && returnOrderService && (
              <Can requiredPermission="return_orders:write">
                <Tooltip
                  title={
                    finalizeBlocked
                      ? t('shared.return_orders.detail.finalize_blocked', {
                          defaultValue: '尚有未完成的驗收單',
                        })
                      : ''
                  }
                >
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={!returnOrder.finalizable}
                    loading={finalizeLoading}
                    onClick={handleFinalize}
                    style={{ marginRight: 8 }}
                  >
                    {t('shared.return_orders.detail.finalize', { defaultValue: '結案驗收' })}
                  </Button>
                </Tooltip>
              </Can>
            )}
            {role === 'operator' && returnOrder.unfinalizable && returnOrderService && (
              <Can requiredPermission="return_orders:write">
                <Button
                  danger
                  icon={<RollbackOutlined />}
                  loading={unfinalizeLoading}
                  onClick={handleUnfinalize}
                  style={{ marginRight: 8 }}
                >
                  {t('shared.return_orders.detail.unfinalize', { defaultValue: '撤回結案' })}
                </Button>
              </Can>
            )}
            {returnOrder.editable && (
              <Can requiredPermission="return_orders:write">
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                  {t('common.button.edit')}
                </Button>
              </Can>
            )}
          </>
        }
      />

      <Descriptions column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('models.return_order.name')}>
          {returnOrder.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.return_order.status')}>
          {getStatusTag(returnOrder.status)}
        </Descriptions.Item>

        {role === 'operator' && returnOrder.merchant && (
          <Descriptions.Item label={t('models.return_order.merchant')}>
            {returnOrder.merchant.name || '-'}
          </Descriptions.Item>
        )}

        <Descriptions.Item label={t('models.return_order.order')}>
          {returnOrder.order ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}>
              <Link to={`/orders/${returnOrder.order.id}`}>
                {returnOrder.order.name}
              </Link>
              <a href={`/${role}/orders/${returnOrder.order.id}`} target="_blank" rel="noopener noreferrer">
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          ) : '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('models.return_order.note')} span={2}>
          {returnOrder.note || '-'}
        </Descriptions.Item>

        <Descriptions.Item label={t('common.created_at')}>
          {returnOrder.created_at ? formatDateTime(returnOrder.created_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.updated_at')}>
          {returnOrder.updated_at ? formatDateTime(returnOrder.updated_at) : '-'}
        </Descriptions.Item>
      </Descriptions>

      {returnOrder.items && returnOrder.items.length > 0 && (
        <Card title={t('shared.return_orders.detail.items')} style={{ marginBottom: 16 }}>
          <Table
            columns={itemColumns}
            dataSource={returnOrder.items}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      <Card
        title={t('shared.return_orders.receipts.actual_inventory_section_title', { defaultValue: '驗收實際庫存' })}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {(returnOrder.return_receipts || []).length === 0 ? (
          <Empty description={t('common.table.no_data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {(returnOrder.return_receipts as any[]).map((rr) => (
              <ReturnReceiptCard
                key={rr.id}
                receipt={rr}
                role={role}
                attributeLabelByName={attributeLabelByName}
                onReopen={role === 'operator' && returnReceiptService ? handleReopen : undefined}
                reopenLoadingId={reopenLoadingId}
                onCancel={role === 'operator' && returnReceiptService ? handleCancel : undefined}
                cancelLoadingId={cancelLoadingId}
              />
            ))}
          </Space>
        )}
      </Card>

      {returnOrderService && (
        <Card title={t('shared.video_preview.title', { defaultValue: '影片' })} style={{ marginBottom: 16 }}>
          <VideoPreview orderId={returnOrder.id} orderService={returnOrderService} />
        </Card>
      )}

      {role === 'operator' && returnOrderService?.getSerialNumbers && (
        <Tabs
          destroyInactiveTabPane
          items={[{
            key: 'serial_numbers',
            label: t('models.return_order.tabs.serial_numbers', { defaultValue: '序號' }),
            children: (
              <TableIndex
                columns={serialNumberColumns}
                service={{
                  getRecords: (params: Record<string, unknown>) =>
                    returnOrderService.getSerialNumbers(returnOrder.id, params),
                }}
                searchable={true}
                columnsEditable={false}
                tableId="operatorReturnOrderSerialNumbersTable"
                defaultQuery={{ sorting_field: 'serial_numbers.created_at', sorting_direction: 'desc' }}
                supportRules={[]}
              />
            ),
          }]}
        />
      )}

    </div>
  );
}

interface ReturnReceiptCardProps {
  receipt: any;
  role: 'admin' | 'operator';
  attributeLabelByName: Record<string, string>;
  onReopen?: (id: string) => void;
  reopenLoadingId: string | null;
  onCancel?: (id: string) => void;
  cancelLoadingId: string | null;
}

const RECEIPT_STATUS_COLOR: Record<string, string> = {
  pending: 'default',
  in_progress: 'processing',
  verified: 'processing',
  done: 'success',
  canceled: 'error',
};

function ReturnReceiptCard({
  receipt, role, attributeLabelByName,
  onReopen, reopenLoadingId, onCancel, cancelLoadingId,
}: ReturnReceiptCardProps) {
  const { t } = useTranslation();
  const showReopen = onReopen && receipt.reopenable;
  const showCancel = onCancel && receipt.cancelable;

  const columns: ColumnsType<any> = [
    {
      title: t('models.return_order_item.product'),
      key: 'product',
      render: (_: any, ri: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {ri.product_id ? (
              <Link to={`/products/${ri.product_id}`}>{ri.product_name}</Link>
            ) : (
              ri.product_name
            )}
            {ri.unplanned && (
              <Tag color="orange" style={{ marginLeft: 4 }}>
                {t('models.inbound_item.unplanned')}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>{ri.product_sku}</div>
        </div>
      ),
    },
    {
      title: t('models.return_order_item.inventory_type'),
      dataIndex: 'inventory_type_name',
      key: 'inventory_type_name',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.return_order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.return_order_item.manufacturing_date', { defaultValue: '製造日期' }),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => v ? formatDate(v) : '-',
    },
    {
      title: t('models.return_order_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => v ? formatDate(v) : '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (v: Record<string, unknown> | null) => (
        <CustomAttributesTags attributes={v} attributeLabelByName={attributeLabelByName} />
      ),
    },
    {
      title: t('models.return_order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
      render: (v: number | null) => v ?? 0,
    },
    {
      title: t('models.receipt_item.received_quantity', { defaultValue: '實收' }),
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      align: 'right' as const,
      render: (v: number | null) => v ?? 0,
    },
    {
      title: t('models.return_order_item.note'),
      dataIndex: 'note',
      key: 'note',
      render: (v: string | null) => v || '-',
    },
  ];

  return (
    <Card
      type="inner"
      title={
        <Space size={8}>
          {role === 'operator'
            ? <Link to={`/return_receipts/${receipt.id}`}>{receipt.name}</Link>
            : <span>{receipt.name}</span>}
          <Tag color={RECEIPT_STATUS_COLOR[receipt.status] || 'default'}>
            {t(`enums.return_receipt.status.${receipt.status}`, { defaultValue: receipt.status })}
          </Tag>
          {receipt.verifier_name && (
            <Text type="secondary" style={{ fontSize: 13 }}>{receipt.verifier_name}</Text>
          )}
          {receipt.verified_at && (
            <Text type="secondary" style={{ fontSize: 13 }}>{formatDateTime(receipt.verified_at)}</Text>
          )}
          {receipt.tracking_number && (
            <Text type="secondary" style={{ fontSize: 13 }}>#{receipt.tracking_number}</Text>
          )}
        </Space>
      }
      extra={
        (showReopen || showCancel) ? (
          <Space size={4}>
            {showReopen && (
              <Can requiredPermission="return_orders:write">
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  loading={reopenLoadingId === receipt.id}
                  onClick={() => onReopen!(receipt.id)}
                >
                  {t('shared.return_orders.detail.continue_verify_button')}
                </Button>
              </Can>
            )}
            {showCancel && (
              <Can requiredPermission="return_orders:write">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={cancelLoadingId === receipt.id}
                  onClick={() => onCancel!(receipt.id)}
                >
                  {t('shared.return_orders.detail.void_receipt_button')}
                </Button>
              </Can>
            )}
          </Space>
        ) : null
      }
      styles={{ body: { padding: 0 } }}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={receipt.return_receipt_items || []}
        pagination={false}
        size="small"
      />
    </Card>
  );
}
